import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, CircularProgress, InputBase, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '@/contexts/LocaleContext'
import { dashboardService, type DashboardActivityEvent } from '@/services/dashboardService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import SectionHeader from '@/components/researcher/SectionHeader'
import { activityMeta } from '@/components/researcher/activityMeta'

type KindFilter = 'all' | 'completed' | 'started' | 'createdStudy' | 'reachedRespondents'
type PeriodFilter = '24h' | '7d' | '30d'

const KIND_FILTERS: KindFilter[] = ['all', 'completed', 'started', 'createdStudy', 'reachedRespondents']
const PERIOD_FILTERS: PeriodFilter[] = ['24h', '7d', '30d']
const PERIOD_HOURS: Record<PeriodFilter, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

export default function ActivityFeedPage() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { language } = useLocale()
  const navigate = useNavigate()

  // undefined = loading, null = failed
  const [events, setEvents] = useState<DashboardActivityEvent[] | null | undefined>(undefined)
  const [kind, setKind] = useState<KindFilter>('all')
  const [period, setPeriod] = useState<PeriodFilter>('30d')
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    dashboardService
      .getActivity(30)
      .then((data) => !cancelled && setEvents(data.events))
      .catch(() => !cancelled && setEvents(null))
    return () => {
      cancelled = true
    }
  }, [])

  const formatters = useMemo(() => {
    const dayFmt = new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long' })
    const timeFmt = new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit' })
    return { dayFmt, timeFmt }
  }, [language])

  /** Display label for the event (verb already translated, target appended). */
  const eventTarget = (e: DashboardActivityEvent) =>
    e.kind === 'reachedRespondents' && e.target_n != null
      ? t('recentActivity.goal', { n: e.target_n })
      : e.target

  const periodEvents = useMemo(() => {
    const cutoff = Date.now() - PERIOD_HOURS[period] * 3600_000
    return (events ?? []).filter((e) => new Date(e.at).getTime() >= cutoff)
  }, [events, period])

  const searchedEvents = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return periodEvents
    return periodEvents.filter((e) =>
      [e.who, e.target, t(`recentActivity.verb.${e.kind}`, { count: e.count ?? 0 })]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(q)),
    )
  }, [periodEvents, query, t])

  const counts = useMemo(
    () =>
      Object.fromEntries(
        KIND_FILTERS.map((f) => [f, f === 'all' ? searchedEvents.length : searchedEvents.filter((e) => e.kind === f).length]),
      ) as Record<KindFilter, number>,
    [searchedEvents],
  )

  const visibleEvents = useMemo(
    () => (kind === 'all' ? searchedEvents : searchedEvents.filter((e) => e.kind === kind)),
    [searchedEvents, kind],
  )

  // Group by calendar day, newest first (the API already sorts desc).
  const groups = useMemo(() => {
    const byDay = new Map<string, DashboardActivityEvent[]>()
    for (const e of visibleEvents) {
      const d = new Date(e.at)
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      const list = byDay.get(key)
      if (list) list.push(e)
      else byDay.set(key, [e])
    }
    const today = new Date()
    const yesterday = new Date(today.getTime() - 24 * 3600_000)
    const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    return Array.from(byDay.entries()).map(([key, list]) => {
      const label =
        key === dayKey(today)
          ? t('activityPage.today')
          : key === dayKey(yesterday)
            ? t('activityPage.yesterday')
            : formatters.dayFmt.format(new Date(list[0].at))
      return { key, label, list }
    })
  }, [visibleEvents, formatters, t])

  const meta = activityMeta(c)
  const loading = events === undefined

  return (
    <ResearcherShell active="activity" crumbs={[t('nav.activity')]} onPrimaryAction={() => navigate('/studies/new')}>
      <Stack spacing={3} sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box>
          <Typography variant="h1" sx={{ fontSize: 32 }}>
            {t('activityPage.title')}
          </Typography>
          <Typography sx={{ fontSize: 15, color: c.ink56, mt: 0.5 }}>{t('activityPage.subtitle')}</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
            }}
          >
            <SectionHeader
              title={t('activityPage.feedTitle')}
              subtitle={t('activityPage.eventCount', { count: visibleEvents.length })}
              right={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: c.gray50,
                    borderRadius: 34,
                    width: 240,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16, color: c.ink48 }} />
                  <InputBase
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('activityPage.searchPlaceholder')}
                    sx={{ flex: 1, fontSize: 12.5 }}
                  />
                </Box>
              }
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {KIND_FILTERS.map((f) => {
                const active = kind === f
                return (
                  <Chip
                    key={f}
                    onClick={() => setKind(f)}
                    label={
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        {t(`activityPage.filter.${f}`)}
                        <Box
                          component="span"
                          sx={{
                            px: 0.75,
                            borderRadius: 34,
                            bgcolor: active ? 'rgba(255,255,255,.2)' : c.gray100,
                            color: active ? '#fff' : c.ink56,
                            fontSize: 11,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {counts[f]}
                        </Box>
                      </Box>
                    }
                    variant={active ? 'filled' : 'outlined'}
                    sx={{
                      height: 32,
                      bgcolor: active ? c.ink : '#fff',
                      color: active ? '#fff' : c.ink72,
                      borderColor: active ? c.ink : c.ink08,
                      '&:hover': { bgcolor: active ? c.ink : c.gray50 },
                    }}
                  />
                )
              })}

              <Box sx={{ flex: 1 }} />

              {PERIOD_FILTERS.map((p) => {
                const active = period === p
                return (
                  <Chip
                    key={p}
                    onClick={() => setPeriod(p)}
                    label={t(`activityPage.period.${p}`)}
                    variant={active ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      height: 28,
                      bgcolor: active ? c.coral : '#fff',
                      color: active ? '#fff' : c.ink72,
                      borderColor: active ? c.coral : c.ink08,
                      '&:hover': { bgcolor: active ? c.coral : c.gray50 },
                    }}
                  />
                )
              })}
            </Box>

            {visibleEvents.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: c.gray50, display: 'grid', placeItems: 'center' }}>
                  <HistoryOutlinedIcon sx={{ fontSize: 24, color: c.ink48 }} />
                </Box>
                <Typography sx={{ fontSize: 13.5, color: c.ink56 }}>
                  {events === null ? t('activityPage.loadFailed') : t('activityPage.empty')}
                </Typography>
              </Box>
            )}

            {groups.map((group) => (
              <Box key={group.key}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: c.ink48,
                    pt: 0.5,
                    pb: 0.75,
                    borderBottom: `1px solid ${c.ink08}`,
                  }}
                >
                  {group.label}
                </Typography>
                {group.list.map((e, i) => {
                  const { Icon, color } = meta[e.kind]
                  const target = eventTarget(e)
                  return (
                    <Box
                      key={e.id}
                      onClick={() => e.project_id && navigate(`/studies/${e.project_id}`)}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        py: 1.25,
                        px: 0.5,
                        borderBottom: i < group.list.length - 1 ? `1px dashed ${c.ink08}` : 'none',
                        minWidth: 0,
                        cursor: e.project_id ? 'pointer' : 'default',
                        borderRadius: '10px',
                        transition: 'background-color 120ms',
                        '&:hover': e.project_id ? { bgcolor: c.gray50 } : undefined,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: `${theme.affexy.radii.sm + 1}px`,
                          bgcolor: c.gray50,
                          display: 'grid',
                          placeItems: 'center',
                          flex: '0 0 28px',
                          color,
                        }}
                      >
                        <Icon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ flex: 1, fontSize: 13, lineHeight: 1.4, minWidth: 0 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {e.who}
                        </Box>{' '}
                        <Box component="span" sx={{ color: c.ink72 }}>
                          {t(`recentActivity.verb.${e.kind}`, { count: e.count ?? 0 })}
                        </Box>
                        {target && (
                          <>
                            {' '}
                            <Box component="span" sx={{ color: c.coral, fontWeight: 500 }}>
                              {target}
                            </Box>
                          </>
                        )}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11.5, color: c.ink48, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                      >
                        {formatters.timeFmt.format(new Date(e.at))}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Paper>
        )}
      </Stack>
    </ResearcherShell>
  )
}
